// App Migration Bottom Sheet Component
// Displays a bottom sheet popup encouraging users to download the app
// Design based on Figma: https://www.figma.com/design/Wq06HKf8WRykqPc8hy8nAn/mweb-App-Migration?node-id=131-4188

import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Helpers
import { hideOverflowY, resetOverflowY } from 'shared/helpers/dom';
import { trackAppMigrationClick } from 'shared/components/AppMigration/analytics';
import type { AppMigrationInteractionLocation } from 'shared/components/AppMigration/analytics';

// Hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// Styles
import {
  Overlay,
  SheetContainer,
  ImageContainer,
  BannerImage,
  AppLogo,
  ContentSection,
  TextContainer,
  Title,
  Subtitle,
  SubtitleRotator,
  SubtitleTrack,
  SubtitleItem,
  Description,
  DownloadButton,
  CloseButton,
} from './styles';

// Icons
import { FashionLogoLarge, CloseCircleBoldIcon } from 'shared/components/AppMigration/icons';

// Types
export interface IAppMigrationBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload?: () => void;
  ctaText?: string;
  /**
   * Analytics interaction_location for download CTA,
   * e.g. `homepage:floater`, `plp:banner`.
   */
  analyticsInteractionLocation?: AppMigrationInteractionLocation;
}

// Default asset URL for bottom sheet background image (includes phone mockups)
const DEFAULT_BG_URL =
  'https://images-static.nykaa.com/nykdesignstudio-images/pub/media/fashion-images/bottomSheet_appMigration.png';

/**
 * Preload images to browser cache
 * Can be called externally to preload before sheet opens
 */
const preloadImages = (urls: string[]): Promise<void[]> => {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Don't block on error
          img.src = url;
        })
    )
  );
};

/**
 * Preload bottom sheet images - call this when banner mounts for faster loading
 */
export const preloadBottomSheetImages = (): void => {
  if (typeof window !== 'undefined') {
    preloadImages([DEFAULT_BG_URL]);
  }
};

// Animation duration for close animation
const CLOSE_ANIMATION_DURATION = 300;

/**
 * AppMigrationBottomSheet - A bottom sheet popup that encourages users to download the app
 *
 * Features:
 * - Dark navy themed header with phone mockups
 * - Nykaa Fashion logo overlapping the header
 * - Clear CTA to download the app
 * - Smooth slide-up/down animations
 * - Overlay click to close
 *
 * @param isOpen - Whether the bottom sheet is visible
 * @param onClose - Callback when the sheet is closed
 * @param onDownload - Callback when download button is clicked
 * @param ctaText - Custom CTA button text (should match banner's CTA text)
 */
function AppMigrationBottomSheet({
  isOpen,
  onClose,
  onDownload,
  ctaText: ctaTextProp,
  analyticsInteractionLocation,
}: IAppMigrationBottomSheetProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const preloadedRef = useRef(false);

  // Rotating subtitle state - all items stay mounted for reliable CSS transitions
  const [activeSubtitleIndex, setActiveSubtitleIndex] = useState(0);
  const [disableTransition, setDisableTransition] = useState(false);
  const subtitleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subtitleSnapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref for close animation timer
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subtitle rotation interval (ms)
  const SUBTITLE_DISPLAY_DURATION = 2500; // how long each text stays visible
  const SUBTITLE_TRANSITION_DURATION = 400; // matches CSS transition duration

  // Remote config for content
  const appMigrationContent = useRemoteConfig('appMigrationContent');
  const bottomSheetConfig = appMigrationContent?.bottomSheet;

  // Content from remote config with fallbacks
  const title = bottomSheetConfig?.title || 'Get the Nykaa Fashion app';
  const subtitles = bottomSheetConfig?.subtitles || [
    'Order Tracking ・ Exclusive Offers',
    'Early Access to Sales',
  ];
  const description =
    bottomSheetConfig?.description || 'Install now to get exciting app-only offers';
  const ctaText = ctaTextProp || bottomSheetConfig?.ctaText || 'Download Now';

  // Background image URL from remote config with fallback
  const bgUrl = bottomSheetConfig?.bgUrl || DEFAULT_BG_URL;

  const imageUrls = [bgUrl];

  // Preload images on first render (before sheet opens)
  useEffect(() => {
    const controller = new AbortController();
    if (!preloadedRef.current && typeof window !== 'undefined') {
      preloadedRef.current = true;
      preloadImages(imageUrls).then(() => {
        if (controller.signal.aborted) return;
        setImagesLoaded(true);
      });
    }
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      hideOverflowY('body', false);
      return () => {
        resetOverflowY();
      };
    }
  }, [isOpen]);

  // Rotating subtitle animation - seamless loop
  // Track has a clone of the first item appended at the end.
  // When we reach the clone, we animate to it normally, then instantly
  // snap back to index 0 (same visual position) with transition disabled.
  useEffect(() => {
    if (!isOpen || subtitles.length <= 1) return;

    subtitleTimerRef.current = setInterval(() => {
      setActiveSubtitleIndex((prev) => {
        const nextIndex = prev + 1;

        if (nextIndex === subtitles.length) {
          // Animate to the cloned first item at the end
          // After the transition, snap back to real index 0
          subtitleSnapTimerRef.current = setTimeout(() => {
            setDisableTransition(true);
            setActiveSubtitleIndex(0);
            // Re-enable transition after the snap
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setDisableTransition(false);
              });
            });
          }, SUBTITLE_TRANSITION_DURATION);
        }

        return nextIndex;
      });
    }, SUBTITLE_DISPLAY_DURATION + SUBTITLE_TRANSITION_DURATION);

    return () => {
      if (subtitleTimerRef.current) {
        clearInterval(subtitleTimerRef.current);
        subtitleTimerRef.current = null;
      }
      if (subtitleSnapTimerRef.current) {
        clearTimeout(subtitleSnapTimerRef.current);
        subtitleSnapTimerRef.current = null;
      }
      setActiveSubtitleIndex(0);
      setDisableTransition(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subtitles.length]);

  // Cleanup close animation timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Handle close with animation
   */
  const handleClose = useCallback(() => {
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, CLOSE_ANIMATION_DURATION);
  }, [onClose]);

  /**
   * Handle overlay click
   */
  const handleOverlayClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  /**
   * Handle download button click
   */
  const handleDownload = useCallback(() => {
    if (analyticsInteractionLocation) {
      trackAppMigrationClick({ interactionLocation: analyticsInteractionLocation });
    }

    if (onDownload) {
      onDownload();
    }
    // Close the sheet after triggering download
    handleClose();
  }, [analyticsInteractionLocation, onDownload, handleClose]);

  /**
   * Prevent click propagation from sheet content
   */
  const handleSheetClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Don't render during SSR
  if (typeof window === 'undefined') {
    return null;
  }

  // Don't render if not open and not in closing animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return createPortal(
    <>
      {/* Overlay */}
      <Overlay
        onClick={handleOverlayClick}
        isClosing={isClosing}
        data-testid="app-migration-bottom-sheet-overlay"
      />

      {/* Bottom Sheet */}
      <SheetContainer
        onClick={handleSheetClick}
        isClosing={isClosing}
        data-testid="app-migration-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-migration-title"
      >
        {/* Close Button - positioned above the sheet */}
        <CloseButton
          onClick={handleClose}
          aria-label="Close"
          data-testid="app-migration-bottom-sheet-close"
        >
          <CloseCircleBoldIcon />
        </CloseButton>

        {/* Image Header - single background image with phone mockups baked in */}
        <ImageContainer>
          <BannerImage
            src={bgUrl}
            alt=""
            loading="eager"
            decoding="async"
            isLoaded={imagesLoaded}
          />
        </ImageContainer>

        {/* App Logo */}
        <AppLogo>
          <FashionLogoLarge />
        </AppLogo>

        {/* Content */}
        <ContentSection>
          <TextContainer>
            <Title id="app-migration-title">{title}</Title>
            <Description>{description}</Description>
            <SubtitleRotator>
              <SubtitleTrack
                activeIndex={activeSubtitleIndex}
                disableTransition={disableTransition}
              >
                {subtitles.map((text: string, index: number) => (
                  <SubtitleItem key={index}>
                    <Subtitle>{text}</Subtitle>
                  </SubtitleItem>
                ))}
                {/* Clone of first item for seamless loop */}
                <SubtitleItem key="clone">
                  <Subtitle>{subtitles[0]}</Subtitle>
                </SubtitleItem>
              </SubtitleTrack>
            </SubtitleRotator>
          </TextContainer>

          <DownloadButton onClick={handleDownload} data-testid="app-migration-download-button">
            {ctaText}
          </DownloadButton>
        </ContentSection>
      </SheetContainer>
    </>,
    document.body
  );
}

export default memo(AppMigrationBottomSheet);
